import * as React from 'react';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { msalFetch } from '../auth/Auth-Utils';
import { constants } from '../../constants/constants';

const fileDownload = require('js-file-download');

/**
 * Reusable CSV export hook shared by every admin list view.
 *
 * Calls an API export endpoint with the same filter payload the list uses,
 * receives the file as a blob, and triggers a browser download. The endpoint
 * sets the real filename via Content-Disposition; `fallbackFilename` is only
 * used if that header is missing.
 *
 * `isExporting` is local to the component using the hook so each export
 * button shows its own progress state independent of the table's loading.
 */
export interface UseCsvExport {
    isExporting: boolean;
    runExport: (
        url: string,
        body: unknown,
        fallbackFilename: string
    ) => Promise<void>;
}

const parseFilename = (
    headers: { [key: string]: string } | undefined,
    fallback: string
): string => {
    const disposition = headers && headers['content-disposition'];
    if (disposition) {
        // e.g. attachment; filename="work-orders-20260519-0930.csv"
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
        if (match && match[1]) {
            return decodeURIComponent(match[1].trim());
        }
    }
    return fallback;
};

export const useCsvExport = (): UseCsvExport => {
    const [isExporting, setIsExporting] = React.useState<boolean>(false);

    const runExport = React.useCallback(
        async (
            url: string,
            body: unknown,
            fallbackFilename: string
        ): Promise<void> => {
            setIsExporting(true);
            try {
                const axiosOptions: AxiosRequestConfig = {
                    method: 'put',
                    data: body,
                    responseType: 'blob'
                };

                const response: AxiosResponse<Blob> = await msalFetch(
                    url,
                    axiosOptions
                );

                if (!response || !response.data) {
                    throw new Error('missing data');
                }

                const filename = parseFilename(
                    response.headers,
                    fallbackFilename
                );
                fileDownload(response.data, filename);
            } catch (error) {
                // With responseType 'blob', a non-2xx error body comes back as
                // a Blob. Convert it to text so the user sees the real server
                // message (e.g. "An error occurred while exporting...") instead
                // of "[object Blob]". Always ends in a visible toast.
                try {
                    const data = error && error.response && error.response.data;
                    if (data && typeof data.text === 'function') {
                        const text: string = await data.text();
                        let detail: string = text;
                        try {
                            const parsed = JSON.parse(text);
                            detail =
                                parsed && typeof parsed === 'object'
                                    ? parsed.value ||
                                      parsed.message ||
                                      parsed.title ||
                                      text
                                    : parsed;
                        } catch (parseErr) {
                            /* body was not JSON - use the raw text */
                        }
                        error.response.data = detail;
                    }
                } catch (readErr) {
                    /* could not read the blob - fall through to generic */
                }
                constants.handleError(error, 'export');
            } finally {
                setIsExporting(false);
            }
        },
        []
    );

    return { isExporting, runExport };
};
